import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, FileText, FileCheck2, DollarSign, Clock, CheckCircle2, Truck, Package } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { gov } from '../../lib/govApi';
import { portalPath } from '../../lib/portal';

const StatCard = ({ icon: Icon, label, value, tone = 'blue', testId }) => (
  <Card className="border-2" data-testid={testId}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-md bg-${tone}-100 text-${tone}-700 flex items-center justify-center`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="text-xl font-bold text-slate-800">{value}</div>
      </div>
    </CardContent>
  </Card>
);

const GovDashboard = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    gov.dashboard().then(r => setData(r.data)).catch(e => setErr(e.response?.data?.detail || 'Failed to load dashboard'));
  }, []);

  if (err) return <div className="p-6 text-red-600" data-testid="gov-dashboard-error">{err}</div>;
  if (!data) return <div className="p-6 text-slate-500">Loading dashboard…</div>;

  const t = data.totals || {};

  return (
    <div className="space-y-6" data-testid="gov-dashboard">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Procurement Dashboard</h1>
        <p className="text-sm text-slate-500">Real-time view of your DES Contract 09925 activity</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={FileText} label="Total orders" value={t.all || 0} testId="stat-total" />
        <StatCard icon={Clock} label="Pending" value={t.pending || 0} tone="amber" testId="stat-pending" />
        <StatCard icon={CheckCircle2} label="Approved" value={t.approved || 0} tone="emerald" testId="stat-approved" />
        <StatCard icon={Truck} label="Shipped" value={t.shipped || 0} tone="indigo" testId="stat-shipped" />
        <StatCard icon={Package} label="Delivered" value={t.delivered || 0} tone="green" testId="stat-delivered" />
        <StatCard icon={DollarSign} label="Total spend" value={`$${(data.spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`} tone="purple" testId="stat-spend" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-2 lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-800">Recent Orders</h2>
              <Link to={portalPath('/orders')}><Button size="sm" variant="outline">View all</Button></Link>
            </div>
            {(data.recent || []).length === 0 ? (
              <p className="text-slate-500 text-sm py-6 text-center">No orders yet. Browse the catalog to place your first order.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr><th className="text-left p-2">Order ID</th><th className="text-left p-2">Status</th><th className="text-left p-2">Total</th><th className="text-left p-2">Date</th></tr>
                </thead>
                <tbody>
                  {(data.recent || []).map(o => (
                    <tr key={o.order_id} className="border-t" data-testid={`gov-dash-order-${o.order_id}`}>
                      <td className="p-2"><Link to={portalPath(`/orders/${o.order_id}`)} className="text-blue-700 underline">{o.order_id}</Link></td>
                      <td className="p-2 capitalize">{(o.status || '').replace(/_/g, ' ')}</td>
                      <td className="p-2 font-semibold">${Number(o.total || 0).toFixed(2)}</td>
                      <td className="p-2 text-slate-500">{new Date(o.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card className="border-2">
          <CardContent className="p-5">
            <h2 className="font-semibold text-slate-800 mb-3">Quick Actions</h2>
            <div className="flex flex-col gap-2">
              <Link to={portalPath('/catalog')}><Button className="w-full bg-blue-700 hover:bg-blue-800 text-white"><ShoppingBag className="h-4 w-4 mr-2" />Browse DES Catalog</Button></Link>
              <Link to={portalPath('/orders')}><Button className="w-full" variant="outline"><FileText className="h-4 w-4 mr-2" />My Orders</Button></Link>
              <Link to={portalPath('/approvals')}><Button className="w-full" variant="outline"><FileCheck2 className="h-4 w-4 mr-2" />Pending Approvals</Button></Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GovDashboard;
